import { useSrdLabel } from './srd-label';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';
import type { HudCharacterData, CharacterHudHelpers, CharacterHudStore, SkillId, HudSpell, AbilityStat } from '../../lib/character-hud-store';
import { SKILL_TO_STAT, statLabel, skillLabel } from '../../lib/character-hud-store';
import type { DragHandleProps } from './types';
import { useI18n } from '../../i18n';
import { useRoll } from '../../hooks/useRoll';
import { RollModeToggle } from './RollModeToggle';
import { DeathBanner, isDeathInputBlocked, isUnconsciousAtZero } from './death-state';
import { performSpellCast, freeSlotLevel, isCastableNow, spellPrepRule } from './spell-cast';
import { SpellAoeAimButton, offerSpellAoe, useSpellAoeAim } from './spell-aoe-aim';
import { HpQuickInput } from './HpQuickInput';
import { HoverDetails, SpellHoverContent, FeatureHoverContent, useHoverDetails } from './hover-details';
import { DND5E_CANONICAL_CONDITIONS, DND5E_CONDITION_LABELS_RU } from '@roll1/shared/systems/dnd5e/conditions.js';

interface CombatStateProps {
  /** GN (re-pass №49): a height that ends above the floating chat when the panel would run under it (default 70vh). */
  maxHeight?: number | string;
  data: HudCharacterData;
  store: CharacterHudStore;
  helpers: CharacterHudHelpers;
  onCollapse: () => void;
  onOpenFullSheet: () => void;
  dragHandleProps: DragHandleProps;
  send?: (msg: any) => void;
  sheetId?: string;
  /** Subscribe to incoming WS messages (V004: cancel roll timeout on roll:result) */
  onMessage?: (handler: (msg: any) => void) => () => void;
  /** Collapsed section ids (persisted in uiPrefs by the overlay). */
  collapsedSections?: string[];
  onToggleSection?: (id: string) => void;
  /** Pinned skill ids for THIS character (persisted in uiPrefs by the overlay). */
  pinnedSkills?: string[];
  onTogglePinSkill?: (skill: string) => void;
  /** Live initiative round from InitiativeContext. The adapter's
   *  data.combat.round reads values.round which nothing ever writes (always 0) —
   *  the overlay passes the real counter. */
  currentRound?: number;
  /** True when the initiative turn belongs to this HUD's character. */
  isMyTurn?: boolean;
  /** The viewer owns the sheet (false: the GM on a player's sheet — the banner names whose turn it is). */
  isOwnSheet?: boolean;
  /** GJ-5: a fight is on (the initiative has entries). After «Reset» the panel stays, the «⚔ бой · раунд» bar goes. Default: on. */
  inCombat?: boolean;
}

type ActionType = 'action' | 'bonus' | 'reaction';

/** Stat cell — paper bg, mono label on top, Fraunces large italic number below.
 *  Matches prototype `.combat-stat`. */
function StatCell({
  label,
  value,
  unit,
  alert,
  isLast,
  onClick,
  testId,
  ariaLabel,
}: {
  label: string;
  value: string;
  unit?: string;
  alert?: boolean;
  isLast?: boolean;
  onClick?: () => void;
  testId?: string;
  ariaLabel?: string;
}) {
  return (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={ariaLabel}
      data-testid={testId}
      onClick={onClick}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
      style={{
        padding: '8px 10px',
        borderRight: isLast ? 'none' : '1px solid var(--paper-rule)',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        cursor: 'pointer',
      }}
    >
      <span className="paper-label">{label}</span>
      <span
        className="paper-num paper-num-md"
        style={{
          color: alert ? 'var(--paper-bordo)' : 'var(--paper-ink)',
        }}
      >
        {value}
        {unit && (
          <small
            className="text-meta" style={{
              fontFamily: 'JetBrains Mono, monospace',
              fontWeight: 400,
              fontStyle: 'normal',
              color: 'var(--paper-ink-2)',
              marginLeft: 2,
            }}
          >
            {unit}
          </small>
        )}
      </span>
    </div>
  );
}

/** Collapsible section header — same mono-uppercase chrome as the old static
 *  labels, plus a disclosure arrow and an item count. The battle panel packs a
 *  lot now (attacks, slots, spells, character) — sections fold to keep it short. */
function SectionHead({
  title,
  count,
  collapsed,
  onToggle,
  testId,
}: {
  title: string;
  count?: number;
  collapsed: boolean;
  onToggle: () => void;
  testId?: string;
}) {
  return (
    <button
      data-testid={testId}
      aria-expanded={!collapsed}
      className="text-label" style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        minHeight: 24,
        margin: '6px 0 2px',
        padding: 0,
        background: 'transparent',
        border: 'none',
        fontFamily: 'JetBrains Mono, monospace',
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: 'var(--paper-ink-2)',
        cursor: 'pointer',
      }}
      onClick={onToggle}
    >
      <span>
        <span style={{ display: 'inline-block', width: 10 }}>{collapsed ? '▸' : '▾'}</span>
        {title}
        {count != null && <span style={{ opacity: 0.7 }}> · {count}</span>}
      </span>
    </button>
  );
}

/** Action economy plate — bordered, opacity-fades when spent. Matches `.combat-econ-tile`. */
function ActionPlate({
  label,
  value,
  spent,
  onToggle,
}: {
  label: string;
  value: string;
  spent: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      className="paper-action-tile"
      style={{
        padding: '6px 8px',
        borderRadius: 3,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        textAlign: 'left',
        opacity: spent ? 0.45 : 1,
      }}
      onClick={onToggle}
    >
      <span className="paper-label text-label" style={{ }}>{label}</span>
      <span className="text-pill" style={{ fontFamily: 'IBM Plex Sans, sans-serif', fontWeight: 500, color: 'var(--paper-ink)' }}>
        {value}
      </span>
    </button>
  );
}

export function CombatState({
  data,
  store,
  helpers,
  onCollapse,
  onOpenFullSheet,
  dragHandleProps,
  send,
  sheetId,
  onMessage,
  collapsedSections = [],
  onToggleSection,
  pinnedSkills = [],
  onTogglePinSkill,
  currentRound,
  isMyTurn,
  isOwnSheet,
  inCombat = true,
  maxHeight = '70vh',
}: CombatStateProps) {
  const { t, locale } = useI18n();
  const { L, Sp, Props } = useSrdLabel();
  // Бывший функционал DockedState (статы/топ-скиллы/фичи) живёт здесь
  // раскрывашкой — отдельное docked-состояние убрано (UX-аудит 2026-07-07).
  const [showCharacter, setShowCharacter] = useState(false);
  const [showHpInput, setShowHpInput] = useState(false);
  // Local fallback when the overlay doesn't wire persistence (tests, demo).
  const [localCollapsed, setLocalCollapsed] = useState<string[]>([]);
  const collapsed = onToggleSection ? collapsedSections : localCollapsed;
  const toggleSection = (id: string) =>
    onToggleSection
      ? onToggleSection(id)
      : setLocalCollapsed((cur) => (cur.includes(id) ? cur.filter((s) => s !== id) : [...cur, id]));
  const isCollapsed = (id: string) => collapsed.includes(id);
  const [skillQuery, setSkillQuery] = useState('');
  // Hover-карточки подробностей заклинаний/способностей (одна на панель).
  const { hoveredId, hoverProps, clearHover } = useHoverDetails();
  // Раскрытие полного списка способностей (по умолчанию — первые 5).
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  // Inline-форма добавления состояния (дропдаун + опциональные раунды).
  const [addingCondition, setAddingCondition] = useState(false);
  const [newCondition, setNewCondition] = useState('');
  const [newConditionRounds, setNewConditionRounds] = useState('');
  const hpPercent = data.hp.max > 0 ? data.hp.current / data.hp.max : 1;
  const isLowHp = hpPercent < 0.35;
  // Batch B (ADR-010 §8.10): a dead character's HUD blocks all mutating input.
  const isDead = isDeathInputBlocked(data);

  // V001: always call useRoll unconditionally (Rules of Hooks)
  const noopSend = useCallback(() => {}, []);
  // T002: HudCharacterData is structurally assignable to RulesCharacterData
  // (see structural contract in src/lib/character-hud-store.ts and
  // shared/systems/dnd5e/rules/index.ts). useRoll's generic accepts the
  // narrower HUD shape directly — no cast required.
  const roll = useRoll(sheetId ?? '', data, send ?? noopSend, onMessage);
  const canRoll = !!(send && sheetId) && !isDead;

  const toggleAction = (type: ActionType) => {
    if (isDead) return;
    // Persists via store.patch → write-adapter → sheet:update {action_economy};
    // server resets it on initiative:next (ActionEconomyResetSubscriber).
    store.patch({ combat: { actionEconomy: { [type]: !data.combat.actionEconomy[type] } } });
  };

  // Auto-spend (этап 1 автоматизации ресурсов, 2026-07-08): casting/attacking
  // MARKS the matching resource spent — it never blocks anything. D&D is full
  // of legal exceptions (Extra Attack, Action Surge, Quickened, held actions),
  // so the system is a memory aid, the GM is the judge; every plate stays a
  // manual toggle. Gated on isMyTurn: outside combat (or off-turn reactions on
  // someone else's turn aside) nothing is tracked.
  const autoSpend = (type: ActionType) => {
    if (!isMyTurn || isDead) return;
    if (!data.combat.actionEconomy[type]) return; // already spent — leave as is
    store.patch({ combat: { actionEconomy: { [type]: false } } });
  };

  const SPELL_ACTION_TO_ECONOMY: Partial<Record<NonNullable<HudSpell['actionType']>, ActionType>> = {
    action: 'action',
    bonus: 'bonus',
    reaction: 'reaction',
    // minute / hour / special — не трекаем (вне экономии хода)
  };

  // Reset the turn's resources to their turn-start state. Turn start IS the
  // fully-reset state (ActionEconomyResetSubscriber refills everything and
  // zeroes movement), so no snapshot is needed — one click undoes a mis-spent
  // action or an accidental move.
  const resetTurnResources = () => {
    if (isDead) return;
    store.patch({
      combat: {
        actionEconomy: { action: true, bonus: true, reaction: true },
        movementSpent: 0,
      },
    });
  };

  const movementSpent = data.combat.movementSpent ?? 0;
  const spendMovement = () => {
    if (isDead) return;
    // Same interaction as the full sheet's tile: +5 ft per click, click at max resets.
    store.patch({
      combat: {
        movementSpent: movementSpent >= data.speed ? 0 : Math.min(movementSpent + 5, data.speed),
      },
    });
  };

  // store.patch is the ONLY server path for slots: the overlay's
  // flush-accumulator diffs spells.slots and write-adapter (T207) emits
  // sheet:spell-slot-spend/-restore itself — same as the full sheet's
  // SpellsSection. The explicit send() that used to live here (V003) predates
  // T207; after it, every pip click spent/restored DOUBLE (send + adapter).
  const spendSlot = (level: number) => {
    if (isDead) return;
    const slot = data.spells.slots[level];
    if (!slot || slot.current <= 0) return;
    store.patch({ spells: { slots: { [level]: { current: slot.current - 1 } } } });
  };

  const restoreSlot = (level: number) => {
    if (isDead) return;
    const slot = data.spells.slots[level];
    if (!slot || slot.current >= slot.max) return;
    store.patch({ spells: { slots: { [level]: { current: slot.current + 1 } } } });
  };

  const mockRoll = (attackName: string, bonus: number) => {
    if (isDead) return;
    const r = Math.floor(Math.random() * 20) + 1;
    const total = r + bonus;
    toast(t('characterHud.mockRoll'), {
      description: `${attackName}: 1d20${helpers.fmt(bonus)} = ${total} (${r})`,
    });
  };

  const slotLevels = Object.keys(data.spells.slots).map(Number).sort((a, b) => a - b);

  // Prepared/always spells, grouped by level ascending (battle panel shows
  // castable spells only — the full grimoire lives in the sheet's SpellsSection).
  const preparedSpells = useMemo(() => {
    // Cantrips are always at hand (they were missing from the battle panel — charsheet pass №36).
    const rule = spellPrepRule(data);
    const list = data.spells.known.filter((s) => isCastableNow(s, rule));
    return [...list].sort((a, b) => a.level - b.level || a.name.localeCompare(b.name));
  }, [data]);

  const spellAim = useSpellAoeAim(sheetId);

  const castSpell = (spell: HudSpell) => {
    if (isDead || !send) return;
    if (isUnconsciousAtZero(data)) { toast(t('characterHud.unconsciousNoAct')); return; }
    // Close the hover card: casting the last slot disables the row under the
    // cursor, and a disabled control never fires mouseleave — the card stuck
    // open until the next hover elsewhere.
    clearHover();
    if (!performSpellCast(spell, { data, send, rollSpellAttack: roll.rollSpellAttack, rollCustom: roll.rollCustom, spendSlot, t, sheetId, label: L })) return;
    offerSpellAoe(spell, spellAim, t);
    // Auto-mark the matching action-economy resource (on my turn only; a
    // ritual cast still goes through the normal slot path here, so it counts).
    const economy = spell.actionType ? SPELL_ACTION_TO_ECONOMY[spell.actionType] : 'action';
    if (economy) autoSpend(economy);
  };

  // Skills: pinned first, then top-by-modifier up to 5 rows total; a search
  // query switches to filtering the FULL skill list (quick roll for anything
  // not currently shown). Pin toggles persist per character via the overlay.
  const allSkills = Object.keys(SKILL_TO_STAT) as SkillId[];
  const visibleSkills = useMemo(() => {
    const q = skillQuery.trim().toLowerCase();
    if (q) {
      return allSkills.filter((s) => skillLabel(t, s).toLowerCase().includes(q));
    }
    const pinned = pinnedSkills.filter((s): s is SkillId => (allSkills as string[]).includes(s));
    const rest = allSkills
      .filter((s) => !pinned.includes(s))
      .sort((a, b) => helpers.activeSkillMod(data, b) - helpers.activeSkillMod(data, a));
    return [...pinned, ...rest.slice(0, Math.max(0, 5 - pinned.length))];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skillQuery, pinnedSkills, data, t]);

  const SAVE_STATS: readonly AbilityStat[] = ['str', 'dex', 'con', 'int', 'wis', 'cha'];

  // Conditions live in sheet.values.conditions (canonical EN keys); the HUD
  // sees them bridged into activeEffects with source==='condition'. Toggle
  // goes through the server command; the echo re-derives the pills.
  const condLabel = (name: string) =>
    locale === 'ru' ? (DND5E_CONDITION_LABELS_RU[name] ?? name) : name;
  const activeConditionNames = new Set(
    data.activeEffects.filter((e) => e.source === 'condition').map((e) => e.name),
  );
  const availableConditions = DND5E_CANONICAL_CONDITIONS.filter((c) => !activeConditionNames.has(c));
  const canEditConditions = !!(send && sheetId);

  const toggleCondition = (name: string, rounds?: number) => {
    if (!canEditConditions) return;
    send({
      type: 'sheet:condition-toggle',
      sheetId,
      condition: name,
      ...(rounds && rounds > 0 ? { rounds } : {}),
    });
  };

  const submitNewCondition = () => {
    if (!newCondition) return;
    const rounds = parseInt(newConditionRounds, 10);
    toggleCondition(newCondition, Number.isFinite(rounds) && rounds > 0 ? rounds : undefined);
    setAddingCondition(false);
    setNewCondition('');
    setNewConditionRounds('');
  };

  return (
    <div
      style={{
        width: 400,
        maxHeight,
        overflowY: 'auto',
        backgroundColor: 'var(--paper-bg)',
        border: '1px solid var(--paper-rule)',
        borderRadius: 4,
        boxShadow:
          '0 1px 0 rgba(0,0,0,.04) inset, 0 24px 60px -20px rgba(0,0,0,.6), 0 8px 16px -6px rgba(0,0,0,.4)',
      }}
    >
      {/* Chrome bar — bordo bg (combat variant), paper text. Sticky: the panel
          body scrolls (maxHeight 70vh) and the drag handle must stay reachable
          at any scroll offset — it used to scroll away with the content. */}
      <div
        className="text-label" style={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          display: 'flex',
          alignItems: 'center',
          height: 22,
          flexShrink: 0,
          padding: '0 8px',
          background: 'linear-gradient(180deg, #5a1820, var(--paper-bordo))',
          borderBottom: '1px solid rgba(0,0,0,.3)',
          borderRadius: '3px 3px 0 0',
          cursor: 'grab',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          fontFamily: 'JetBrains Mono, monospace',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'rgba(244, 237, 224, .85)',
        }}
        {...dragHandleProps}
      >
        <span style={{ display: 'flex', gap: 2, marginRight: 8 }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <span key={i} style={{ width: 3, height: 3, background: 'rgba(244, 237, 224, .55)', borderRadius: '50%' }} />
          ))}
        </span>
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {data.identity.name} · {L(data.identity.className)} {data.identity.level}
        </span>
        <span style={{ display: 'flex', gap: 4 }}>
          <button
            className="hud-target text-body-s"
            style={{ minWidth: 24, minHeight: 24, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', cursor: 'pointer', padding: '0 4px', color: 'rgba(244, 237, 224, .75)', fontFamily: 'inherit' }}
            onClick={onCollapse} title={t('characterHud.collapse')} aria-label={t('characterHud.collapse')}
          >‒</button>
          <button
            className="hud-target text-body-s"
            style={{ minWidth: 24, minHeight: 24, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', cursor: 'pointer', padding: '0 4px', color: showCharacter ? 'rgba(244, 237, 224, 1)' : 'rgba(244, 237, 224, .75)', fontFamily: 'inherit' }}
            aria-label={t('characterHud.showCharacter')}
            onClick={() => setShowCharacter((v) => !v)} title={t('characterHud.showCharacter')}
            aria-pressed={showCharacter}
            data-testid="battle-character-toggle"
          >▤</button>
          <button
            className="hud-target text-title"
            style={{ minWidth: 24, minHeight: 24, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', cursor: 'pointer', padding: '0 4px', color: 'rgba(244, 237, 224, .75)', fontFamily: 'inherit' }}
            onClick={onOpenFullSheet} title={t('characterHud.openFullSheet')} aria-label={t('characterHud.openFullSheet')}
          >⛶</button>
        </span>
      </div>

      {/* GN (Н4): advantage / disadvantage for the next d20 roll */}
      <RollModeToggle sheetId={sheetId} disabled={!canRoll} className="px-3 pt-2" />

      <DeathBanner data={data} />

      {/* Combat banner — bordo bar. Sticky right under the chrome bar (22px)
          so it stays visible while the panel body scrolls. «Твой ход» only
          when it actually IS this character's initiative turn; the round comes
          from live initiative (adapter's data.combat.round is always 0). */}
      {inCombat && (
      <div
        className="paper-banner"
        data-testid="combat-banner"
        style={{
          position: 'sticky',
          top: 22,
          zIndex: 1,
          padding: '6px 12px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span style={{ fontWeight: 600 }}>
          {isMyTurn ? (isOwnSheet === false ? t('characterHud.combatBannerTurnOf').replace('{name}', data.identity.name) : t('characterHud.combatBanner')) : t('characterHud.combatBannerIdle')}
        </span>
        <span className="text-label" style={{ opacity: 0.7 }}>
          {t('characterHud.round')} {currentRound ?? data.combat.round} · {data.speed} {t('characterHud.tileSpeed')}
        </span>
      </div>
      )}

      {/* 3 stat tiles with vertical dividers */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          borderBottom: '1px solid var(--paper-rule)',
        }}
      >
        <StatCell label={t('characterHud.tileHp')} value={String(data.hp.current)} unit={`/${data.hp.max}${data.hp.temp > 0 ? ` +${data.hp.temp}` : ''}`} alert={isLowHp}
          testId="hud-hp-tile" ariaLabel={t('characterHud.hpInputLabel')} onClick={send && sheetId ? () => setShowHpInput((v) => !v) : undefined} />
        <StatCell label={t('characterHud.tileAc')} value={String(data.ac)} />
        <StatCell label={t('characterHud.tileSpeed')} value={String(data.speed)} unit="фт" isLast />
      </div>

      {/* Damage / healing right in the HUD (charsheet pass №11): tap the hit points tile. */}
      {showHpInput && (
        <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--paper-rule)' }}>
          <HpQuickInput data={data} store={store} send={send} sheetId={sheetId} compact />
        </div>
      )}

      {/* Action economy — 3 outlined plates */}
      <div style={{ padding: '10px 12px 6px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
        <ActionPlate
          label={t('characterHud.actionAction')}
          value={data.combat.actionEconomy.action ? t('characterHud.available') : t('characterHud.spent')}
          spent={!data.combat.actionEconomy.action}
          onToggle={() => toggleAction('action')}
        />
        <ActionPlate
          label={t('characterHud.actionBonus')}
          value={data.combat.actionEconomy.bonus ? t('characterHud.available') : t('characterHud.spent')}
          spent={!data.combat.actionEconomy.bonus}
          onToggle={() => toggleAction('bonus')}
        />
        <ActionPlate
          label={t('characterHud.actionReaction')}
          value={data.combat.actionEconomy.reaction ? t('characterHud.available') : t('characterHud.spent')}
          spent={!data.combat.actionEconomy.reaction}
          onToggle={() => toggleAction('reaction')}
        />
      </div>

      {/* Movement + turn-reset (этап 1 автоматизации): клик по движению = +5 фт
          (на максимуме — сброс, как в полном листе); ⟲ возвращает ВСЕ ресурсы
          хода к состоянию начала хода — быстрый откат «не так походил». */}
      <div style={{ padding: '0 12px 6px', display: 'flex', gap: 6 }}>
        <button
          className={`paper-action-tile ${movementSpent >= data.speed ? 'spent' : ''}`}
          style={{
            flex: 1,
            padding: '6px 8px',
            borderRadius: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            opacity: movementSpent >= data.speed ? 0.45 : 1,
          }}
          onClick={spendMovement}
          data-testid="battle-movement"
          title={t('characterHud.movementHint')}
        >
          <span className="paper-label text-label" style={{ }}>{t('characterHud.movement')}</span>
          {/* Overspend (Dash etc.) shows as a NEGATIVE remainder — clamping to 0
              hid that the budget was blown (owner feedback 2026-07-08). */}
          <span
            className="paper-mono text-pill"
            style={{ color: data.speed - movementSpent < 0 ? 'var(--paper-bordo)' : undefined }}
          >
            {data.speed - movementSpent}/{data.speed} {t('characterHud.tileSpeed')}
          </span>
        </button>
        <button
          className="paper-action-tile text-body-s"
          style={{ padding: '6px 10px', borderRadius: 3 }}
          onClick={resetTurnResources}
          data-testid="battle-turn-reset"
          title={t('characterHud.resetTurn')}
        >⟲</button>
      </div>

      {/* Attacks list */}
      {data.combat.attacks.length > 0 && (
        <div style={{ padding: '4px 12px 10px' }}>
          <SectionHead
            title={t('characterHud.attacks')}
            count={data.combat.attacks.length}
            collapsed={isCollapsed('attacks')}
            onToggle={() => toggleSection('attacks')}
            testId="battle-section-attacks"
          />
          {!isCollapsed('attacks') && data.combat.attacks.map((atk) => (
            <button
              key={atk.id}
              className="text-meta" style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto auto',
                gap: 8,
                alignItems: 'center',
                width: '100%',
                padding: '6px 4px',
                borderBottom: '1px dashed var(--paper-rule)',
                cursor: 'pointer',
                background: 'transparent',
                fontFamily: 'IBM Plex Sans, sans-serif',
                color: 'var(--paper-ink)',
                textAlign: 'left',
              }}
              onClick={() => {
                if (isUnconsciousAtZero(data)) { toast(t('characterHud.unconsciousNoAct')); return; }
                if (!canRoll) { mockRoll(L(atk.name), atk.attackBonus); return; }
                roll.rollAttack({ ...atk, name: L(atk.name), damageType: L(atk.damageType) });
                // Mark the action spent (never blocks — Extra Attack keeps rolling).
                autoSpend('action');
              }}
            >
              <span style={{ fontWeight: 500 }}>
                {L(atk.name)}
                {atk.meta && (
                  <small
                    className="text-label" style={{
                      color: 'var(--paper-ink-2)',
                      fontWeight: 400,
                      marginLeft: 4,
                      fontFamily: 'JetBrains Mono, monospace',
                    }}
                  >
                    {Props(atk.meta)}
                  </small>
                )}
              </span>
              <span className="paper-mono text-pill" style={{ color: 'var(--paper-ink-2)' }}>
                {helpers.fmt(atk.attackBonus)}
              </span>
              <span className="paper-mono text-pill" style={{ color: 'var(--paper-bordo)' }}>
                {atk.damage} {L(atk.damageType)}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Spell slots — pip rows */}
      {slotLevels.length > 0 && (
        <div style={{ padding: '0 12px 10px' }}>
          <SectionHead
            title={t('characterHud.spellSlots')}
            collapsed={isCollapsed('slots')}
            onToggle={() => toggleSection('slots')}
            testId="battle-section-slots"
          />
          {!isCollapsed('slots') && slotLevels.map((lvl) => {
            const slot = data.spells.slots[lvl];
            return (
              <div key={lvl} className="text-pill" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0' }}>
                <span className="paper-label" style={{ minWidth: 24 }}>
                  {lvl}й
                </span>
                <div style={{ display: 'flex', gap: 6 }}>
                  {Array.from({ length: slot.max }, (_, i) => (
                    <button
                      key={i}
                      className={`paper-pip ${i < slot.current ? 'filled' : ''}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => (i < slot.current ? spendSlot(lvl) : restoreSlot(lvl))}
                      aria-label={`${t('characterHud.spellSlots')} ${lvl}`}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Prepared spells — tap to cast (attack spells roll to-hit, save/utility
          spells post a spell-cast chat card; leveled casts spend a slot). */}
      {preparedSpells.length > 0 && (
        <div style={{ padding: '0 12px 10px' }}>
          <SectionHead
            title={t('characterHud.spells')}
            count={preparedSpells.length}
            collapsed={isCollapsed('spells')}
            onToggle={() => toggleSection('spells')}
            testId="battle-section-spells"
          />
          {data.spells.concentrationSpellId && (
            <div data-testid="hud-concentration" className="text-pill" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0' }}>
              <span style={{ flex: 1, color: 'var(--paper-gold)' }}>
                {t('characterHud.concentrationOn').replace('{spell}', data.spells.known.find((s) => (s.spellSlug ?? s.id) === data.spells.concentrationSpellId)?.name ?? data.spells.concentrationSpellId)}
              </span>
              {send && sheetId && !isDead && (
                <button type="button" onClick={() => send({ type: 'sheet:concentration-set', sheetId, spellSlug: null })}
                  className="text-pill" style={{ minHeight: 24, minWidth: 24, padding: '0 8px', border: '1px solid var(--paper-rule)', borderRadius: 3, background: 'var(--paper-bg)', cursor: 'pointer' }}>
                  {t('characterHud.concentrationEnd')}
                </button>
              )}
            </div>
          )}
          {!isCollapsed('spells') && preparedSpells.map((spell, i) => {
            const noSlot = spell.level > 0 && freeSlotLevel(data, spell.level) === null;
            return (
              <div key={`${spell.id}:${i}`} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <HoverDetails
                open={hoveredId === `spell:${i}`}
                content={<SpellHoverContent spell={spell} />}
              >
              <button
                className="text-meta" style={{
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr auto',
                  gap: 8,
                  alignItems: 'center',
                  width: '100%',
                  padding: '5px 4px',
                  borderBottom: '1px dashed var(--paper-rule)',
                  cursor: noSlot ? 'not-allowed' : 'pointer',
                  opacity: noSlot ? 0.45 : 1,
                  background: 'transparent',
                  fontFamily: 'IBM Plex Sans, sans-serif',
                  color: 'var(--paper-ink)',
                  textAlign: 'left',
                }}
                aria-disabled={noSlot || undefined}
                title={noSlot ? t('characterHud.noSlotForSpell').replace('{spell}', spell.name).replace('{n}', String(spell.level)) : undefined}
                onClick={() => castSpell(spell)}
                {...hoverProps(`spell:${i}`)}
              >
                <span
                  className="paper-mono text-label"
                  style={{ color: 'var(--paper-ink-2)', minWidth: 14, textAlign: 'center' }}
                >
                  {spell.level === 0 ? '·' : spell.level}
                </span>
                <span style={{ fontWeight: 500 }}>
                  {L(spell.name)}
                  {spell.concentration && (
                    <small className="text-label" style={{ color: 'var(--paper-gold)', marginLeft: 4, fontFamily: 'JetBrains Mono, monospace' }}>К</small>
                  )}
                  {spell.ritual && (
                    <small className="text-label" style={{ color: 'var(--paper-ink-2)', marginLeft: 4, fontFamily: 'JetBrains Mono, monospace' }}>Р</small>
                  )}
                </span>
                <span className="paper-mono text-label" style={{ color: 'var(--paper-ink-2)' }}>
                  {spell.actionType === 'bonus' ? t('characterHud.actionBonus') : Sp(spell.castingTime)}
                </span>
              </button>
              </HoverDetails>
              <SpellAoeAimButton spell={spell} aim={spellAim} />
              </div>
            );
          })}
        </div>
      )}

      {/* Active effects & conditions — colored pills. Conditions are editable
          right here: × removes, «+» opens an add form with an optional round
          count (ticked down by the server at the start of the bearer's turn). */}
      {(data.activeEffects.length > 0 || canEditConditions) && (
        <div
          style={{
            borderTop: '1px solid var(--paper-rule)',
            padding: '6px 12px',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 4,
          }}
          data-testid="battle-conditions"
        >
          {data.activeEffects.map((eff) => {
            const isCondition = eff.source === 'condition';
            return (
              <span
                key={eff.id}
                className="text-label" style={{
                  padding: '2px 8px',
                  borderRadius: 10,
                  fontFamily: 'JetBrains Mono, monospace',
                  background: 'var(--paper-gold)',
                  color: 'var(--paper-bg)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                {isCondition ? condLabel(eff.name) : eff.name}
                {eff.duration && <span style={{ opacity: 0.7 }}>{eff.duration}</span>}
                {isCondition && canEditConditions && (
                  <button
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                      marginLeft: 2,
                      minWidth: 24,
                      minHeight: 24,
                      lineHeight: 1,
                      color: 'var(--paper-bg)',
                      opacity: 0.8,
                    }}
                    className="hud-target text-meta"
                    onClick={() => toggleCondition(eff.name)}
                    title={t('characterHud.conditionRemove')}
                    aria-label={`${t('characterHud.conditionRemove')}: ${condLabel(eff.name)}`}
                    data-testid={`condition-remove-${eff.name}`}
                  >✕</button>
                )}
              </span>
            );
          })}

          {canEditConditions && !addingCondition && (
            <button
              className="text-label" style={{
                padding: '2px 8px',
                borderRadius: 10,
                border: '1px dashed var(--paper-rule)',
                background: 'transparent',
                fontFamily: 'JetBrains Mono, monospace',
                color: 'var(--paper-ink-2)',
                cursor: 'pointer',
              }}
              onClick={() => setAddingCondition(true)}
              data-testid="condition-add-open"
            >
              + {t('characterHud.conditionAdd')}
            </button>
          )}

          {canEditConditions && addingCondition && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }} data-testid="condition-add-form">
              <select
                value={newCondition}
                onChange={(e) => setNewCondition(e.target.value)}
                className="text-pill" style={{
                  padding: '2px 4px',
                  border: '1px solid var(--paper-rule)',
                  borderRadius: 3,
                  background: 'var(--paper-bg)',
                  color: 'var(--paper-ink)',
                  fontFamily: 'IBM Plex Sans, sans-serif',
                  maxWidth: 140,
                }}
                data-testid="condition-add-select"
              >
                <option value="">{t('characterHud.conditionAdd')}…</option>
                {availableConditions.map((c) => (
                  <option key={c} value={c}>{condLabel(c)}</option>
                ))}
              </select>
              <input
                value={newConditionRounds}
                onChange={(e) => setNewConditionRounds(e.target.value.replace(/\D/g, ''))}
                placeholder="∞"
                title={t('characterHud.conditionRounds')}
                inputMode="numeric"
                className="text-pill" style={{
                  width: 34,
                  padding: '2px 4px',
                  border: '1px solid var(--paper-rule)',
                  borderRadius: 3,
                  background: 'transparent',
                  color: 'var(--paper-ink)',
                  textAlign: 'center',
                  fontFamily: 'JetBrains Mono, monospace',
                }}
                data-testid="condition-add-rounds"
              />
              <button
                className="hud-target text-body-s"
                style={{ minWidth: 24, minHeight: 24, background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--paper-ink)' }}
                onClick={submitNewCondition}
                disabled={!newCondition}
                data-testid="condition-add-submit"
                title={t('characterHud.conditionApply')}
              >✓</button>
              <button
                className="hud-target text-body-s"
                style={{ minWidth: 24, minHeight: 24, background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--paper-ink-2)' }}
                onClick={() => { setAddingCondition(false); setNewCondition(''); setNewConditionRounds(''); }}
                title={t('characterHud.collapse')}
              >✕</button>
            </span>
          )}
        </div>
      )}

      {/* «Персонаж» — раскрывашка с контентом бывшего DockedState:
          статы (клик = проверка), топ-скиллы (клик = бросок), фичи. */}
      {showCharacter && (
        <div
          data-testid="battle-character-section"
          style={{ borderTop: '2px solid var(--paper-rule)', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 12 }}
        >
          <div className="flex flex-col gap-1">
            <span className="paper-label">{t('characterHud.stats')}</span>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4 }}>
              {(['str', 'dex', 'con', 'int', 'wis', 'cha'] as const).map((stat) => {
                const val = data.stats[stat];
                const mod = helpers.mod(val);
                return (
                  <button
                    key={stat}
                    style={{
                      padding: 6,
                      border: '1px solid var(--paper-rule)',
                      borderRadius: 3,
                      background: 'transparent',
                      textAlign: 'center',
                      cursor: 'pointer',
                    }}
                    onClick={() => canRoll ? roll.rollAbilityCheck(stat) : mockRoll(statLabel(t, stat), mod)}
                  >
                    <div className="paper-label text-label" style={{ }}>{statLabel(t, stat)}</div>
                    <div className="paper-num text-h1" style={{ }}>{val}</div>
                    <div className="paper-mono text-label" style={{ color: 'var(--paper-ink-2)' }}>{helpers.fmt(mod)}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Saving throws — owner feedback 2026-07-08: needed at the table
              without opening the full sheet. Dot = proficiency. */}
          <div className="flex flex-col gap-1">
            <span className="paper-label">{t('characterHud.saves')}</span>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4 }} data-testid="battle-saves">
              {SAVE_STATS.map((stat) => {
                const mod = helpers.activeSaveMod(data, stat);
                const isProf = data.saves[stat] >= 1;
                return (
                  <button
                    key={stat}
                    style={{
                      padding: '4px 6px',
                      border: '1px solid var(--paper-rule)',
                      borderRadius: 3,
                      background: 'transparent',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer',
                    }}
                    onClick={() => canRoll ? roll.rollSave(stat) : mockRoll(`${statLabel(t, stat)} (сп.)`, mod)}
                  >
                    <span className="paper-label text-label" style={{ }}>
                      {isProf && <span style={{ color: 'var(--paper-bordo)', marginRight: 2 }}>●</span>}
                      {statLabel(t, stat)}
                    </span>
                    <span className="paper-mono text-pill" style={{ }}>{helpers.fmt(mod)}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Skills — pinned first, then top-by-mod; search reaches the FULL
              list so any skill can be rolled or pinned from here. */}
          <div className="flex flex-col gap-1">
            <span className="paper-label">{t('characterHud.skills')}</span>
            <input
              value={skillQuery}
              onChange={(e) => setSkillQuery(e.target.value)}
              placeholder={t('characterHud.searchSkills')}
              data-testid="battle-skill-search"
              className="text-pill" style={{
                width: '100%',
                padding: '4px 6px',
                border: '1px solid var(--paper-rule)',
                borderRadius: 3,
                background: 'transparent',
                fontFamily: 'IBM Plex Sans, sans-serif',
                color: 'var(--paper-ink)',
                outline: 'none',
              }}
            />
            {visibleSkills.map((skill) => {
              const mod = helpers.activeSkillMod(data, skill);
              const isPinned = pinnedSkills.includes(skill);
              return (
                <div key={skill} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 4, alignItems: 'center' }}>
                  <button
                    className="text-meta" style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr auto',
                      gap: 4,
                      padding: '3px 0',
                      background: 'transparent',
                      cursor: 'pointer',
                      color: 'var(--paper-ink)',
                      fontFamily: 'IBM Plex Sans, sans-serif',
                    }}
                    onClick={() => canRoll ? roll.rollSkill(skill) : mockRoll(skillLabel(t, skill), mod)}
                  >
                    <span style={{ textAlign: 'left' }}>{skillLabel(t, skill)}</span>
                    <span className="paper-mono">{helpers.fmt(mod)}</span>
                  </button>
                  {onTogglePinSkill && (
                    <button
                      className="text-pill" style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '0 2px',
                        color: isPinned ? 'var(--paper-gold)' : 'var(--paper-ink-2)',
                        opacity: isPinned ? 1 : 0.5,
                      }}
                      onClick={() => onTogglePinSkill(skill)}
                      title={isPinned ? t('characterHud.unpinSkill') : t('characterHud.pinSkill')}
                      aria-pressed={isPinned}
                      data-testid={`pin-skill-${skill}`}
                    >★</button>
                  )}
                </div>
              );
            })}
          </div>

          {data.features.length > 0 && (
            <div className="flex flex-col gap-1">
              <span className="paper-label">{t('characterHud.features')}</span>
              {/* Hover key is POSITIONAL (`feat:<index>`): sheet abilities can
                  carry duplicate or empty ids (level-up / migration artifacts),
                  and an id-based key then matched EVERY row — hovering one
                  feature opened all the cards at once (2026-07-08). */}
              {(showAllFeatures ? data.features : data.features.slice(0, 5)).map((feat, i) => (
                <HoverDetails
                  key={`${feat.id}:${i}`}
                  open={hoveredId === `feat:${i}`}
                  content={<FeatureHoverContent feature={feat} />}
                >
                  <div
                    style={{ padding: '3px 0', borderBottom: '1px dashed var(--paper-rule)', display: 'grid', gridTemplateColumns: '1fr auto' }}
                    {...hoverProps(`feat:${i}`)}
                  >
                    <span className="text-meta" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: 'var(--paper-ink)' }}>{L(feat.name)}</span>
                    <span className="paper-mono text-label" style={{ color: 'var(--paper-ink-2)' }}>{feat.source}</span>
                  </div>
                </HoverDetails>
              ))}
              {data.features.length > 5 && (
                <button
                  className="paper-label"
                  style={{
                    marginTop: 4,
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                    padding: 0,
                    color: 'var(--paper-ink-2)',
                    textDecoration: 'underline dotted',
                  }}
                  onClick={() => setShowAllFeatures((v) => !v)}
                  aria-expanded={showAllFeatures}
                  data-testid="features-expand"
                >
                  {showAllFeatures
                    ? t('characterHud.collapseList')
                    : `${t('characterHud.moreFeatures')} ${data.features.length - 5}…`}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
